import type { App } from 'vue'
import type { Router } from 'vue-router'
import { CHAVE_OBSERVABILIDADE } from '@next/compartilhados/observabilidade'
import type { ContextoErro, IdentidadeObservabilidade } from '@next/compartilhados/observabilidade'
import { configurarObservabilidade } from '@next/compartilhados/observabilidade/configuracao'
import type { EntradaConfiguracaoObservabilidade } from '@next/compartilhados/observabilidade/configuracao'
import { criarObservabilidadeSentry } from '@next/infraestrutura/observabilidade/sentry.adapter'
import type { ProjetarErroLegado } from '@next/infraestrutura/observabilidade/projecao-erro'

type DependenciasObservabilidade = Readonly<{
  configuracao: EntradaConfiguracaoObservabilidade
  origemAplicacao: string
  normalizarLegado?: (erro: unknown, detalhes: unknown) => ContextoErro | undefined
  obterErroOriginal?: (erro: unknown) => unknown
  projetarErroLegado?: ProjetarErroLegado
  obterIdentidade?: () => IdentidadeObservabilidade | undefined
}>

const instancias = new WeakMap<App, ReturnType<typeof criarObservabilidadeSentry>>()

/** Compor/prover é neutro; ativação e handlers são uma etapa explícita do bootstrap. */
export const prepararObservabilidade = (app: App, dependencias: DependenciasObservabilidade) => {
  const existente = instancias.get(app)
  if (existente) return existente
  const instancia = criarObservabilidadeSentry({
    ...dependencias,
    configuracao: configurarObservabilidade(dependencias.configuracao),
  })
  instancias.set(app, instancia)
  app.provide(CHAVE_OBSERVABILIDADE, instancia.porta)
  app.onUnmount(() => { instancia.desativar() })
  return instancia
}

const rotasInstaladas = new WeakSet<Router>()
export const ativarObservabilidade = (
  app: App,
  router: Router,
  instancia: ReturnType<typeof prepararObservabilidade>,
): void => {
  instancia.ativar(app)
  if (rotasInstaladas.has(router)) return
  rotasInstaladas.add(router)
  const remover = router.onError(erro => {
    instancia.porta.registrarErro(erro, { origem: 'navegacao', operacao: 'navegacao' })
  })
  app.onUnmount(() => { remover(); rotasInstaladas.delete(router) })
}
